import { v4 as uuid } from 'uuid';
import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, type OnInit, signal, ViewChild } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatDesigns from '@medsurf/flat-designs';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-chapter-main-vocabulary-word-dialog',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './vocabulary-word-dialog.component.html',
    styleUrls: ['./vocabulary-word-dialog.component.scss'],
    standalone: false
})
export class VocabularyWordDialogComponent implements OnInit {
  @ViewChild('keyInputText') keyInputText: FlatDesigns.TextInputFormControlDesign;
  
  isNew = signal(false);
  #isSubmitted = signal(false);
  vocabularyGroup: string;
  vocabularyWord?: FlatModels.VocabularyWordEntityModels.VocabularyWord;
  vocabularyWordForm = new FormGroup(
    {
      key: new FormControl(''),
      value: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.VocabularyWordEntityModels.VocabularyWordSchema.pick({
          key: true,
          value: true,
        })
      ),
      updateOn: 'change',
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public vocabularySharedFacade: FlatFacades.VocabularySharedFacade,
              public dynamicDialogRef: DynamicDialogRef,
              public dynamicDialogConfig: DynamicDialogConfig,
              public dialogService: DialogService,
              public translate: TranslateService) {
    this.vocabularyGroup = this.dynamicDialogConfig.data.vocabularyGroup;
    if (this.dynamicDialogConfig.data && this.dynamicDialogConfig.data.vocabularyWord) {
      this.vocabularyWord = structuredClone(this.dynamicDialogConfig.data.vocabularyWord);
    }
    
    if (this.vocabularyWord) {
      this.isNew.set(false);
      this.vocabularyWordForm.reset(this.vocabularyWord);
    } else {
      this.isNew.set(true);
    }
    this.#isSubmitted.set(false);
  }

  ngOnInit() {
    // Autofocus
    setTimeout(() => {
      this.keyInputText?.inputElement?.nativeElement?.focus();
    })
  }

  onSubmit() {
    if (this.#isSubmitted() || this.vocabularyWordForm.invalid) {
      return;
    }
    if (!this.vocabularyWordForm.touched) {
      this.dynamicDialogRef.close();
      return;
    }
    this.#isSubmitted.set(true);

    if (this.vocabularyWord) {
      // Update Vocabulary Word
      const vocabularyWord = assign(this.vocabularyWord, this.vocabularyWordForm.value);
      this.vocabularySharedFacade.requestUpdateVocabularyWord(vocabularyWord).subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.vocabularyWordForm);
          this.#isSubmitted.set(false);
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.dynamicDialogRef.close();
        }
      });
    } else {
      // Add Vocabulary Word
      const vocabularyWord: FlatModels.VocabularyWordEntityModels.VocabularyWord = {
        id: uuid(),
        vocabularyGroup: this.vocabularyGroup,
        key: this.vocabularyWordForm.value.key || '',
        value: this.vocabularyWordForm.value.value || '',
      }
      this.vocabularySharedFacade.requestAddVocabularyWord(this.vocabularyGroup, vocabularyWord).subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.vocabularyWordForm);
          this.#isSubmitted.set(false);
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.dynamicDialogRef.close();
        }
      })
    }
  }

  deleteVocabularyWord() {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.vocabulary_word_remove'),
      data: {
        label: 'global.vocabulary_word_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        if (this.vocabularyWord) {
          this.vocabularySharedFacade.requestRemoveVocabularyWord(this.vocabularyGroup, this.vocabularyWord).subscribe({
            error: (result) => {
              ZodSharedValidator.handleError(result, this.alertControlFacade, this.vocabularyWordForm);
            },
            next: () => {
              this.dynamicDialogRef.close();
            }
          });
        }
      }
    });
  }
}
